import { useState } from "react";
import Iconify from "src/components/Iconify";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/@/components/ui/tabs";
import { ASSINATURA_DESIGN as d } from "./Assinatura.design";

const MOCK_DATA = {
    status: "trial",
    planName: "Profissional",
    price: 497,
    trialEnd: "28 de março de 2026",
    addons: [
        {
            id: 1,
            name: "Agentes de IA",
            description: "Agentes inteligentes para automação",
            icon: "mingcute:head-ai-fill",
            status: "trial",
        },
        {
            id: 2,
            name: "Automações avançadas",
            description: "Workflows e triggers personalizados",
            icon: "solar:bolt-bold",
            status: "trial",
        },
        {
            id: 3,
            name: "Múltiplos canais",
            description: "WhatsApp, Instagram, Facebook e mais",
            icon: "fluent:channel-share-28-filled",
            status: "trial",
        },
    ],
};

const StatusBadge = ({ status }) => {
    const map = {
        active: { label: "Ativo", badge: d.subscription.statusActive, dot: d.subscription.dotActive },
        trial: { label: "Avaliação", badge: d.subscription.statusTrial, dot: d.subscription.dotTrial },
        inactive: { label: "Inativo", badge: d.subscription.statusInactive, dot: d.subscription.dotInactive },
    };
    const s = map[status] || map.inactive;
    return (
        <span className={`${d.subscription.statusBadge} ${s.badge}`}>
            <span className={`${d.subscription.dot} ${s.dot}`} />
            {s.label}
        </span>
    );
};

const TabPlaceholder = ({ icon, title, subtitle }) => (
    <div className={d.placeholder.container}>
        <div className={d.placeholder.iconWrapper}>
            <Iconify icon={icon} width={28} className={d.placeholder.icon} />
        </div>
        <p className={d.placeholder.title}>{title}</p>
        <p className={d.placeholder.subtitle}>{subtitle}</p>
    </div>
);

const SubscriptionTab = () => {
    const data = MOCK_DATA;
    return (
        <div className="flex flex-col gap-5">
            {/* Status Card */}
            <div className={d.subscription.statusCard}>
                <div className={d.subscription.statusHeader}>
                    <div className={d.subscription.statusLeft}>
                        <p className={d.subscription.statusLabel}>Status da assinatura</p>
                        <div className="flex items-center gap-3">
                            <StatusBadge status={data.status} />
                        </div>
                        <div className={d.subscription.priceContainer}>
                            <span className={d.subscription.price}>R${data.price}</span>
                            <span className={d.subscription.pricePeriod}>/mês</span>
                        </div>
                        <p className={d.subscription.dateInfo}>
                            {data.status === "trial"
                                ? `Período de avaliação termina em ${data.trialEnd}`
                                : `Próxima cobrança em ${data.trialEnd}`}
                        </p>
                    </div>
                </div>
            </div>

            {/* Plan Card */}
            <div className={d.plan.card}>
                <div className={d.plan.header}>
                    <div className={d.plan.headerLeft}>
                        <div className={d.plan.iconWrapper}>
                            <Iconify icon="solar:crown-bold" width={20} className={d.plan.icon} />
                        </div>
                        <div>
                            <p className={d.plan.title}>{data.planName}</p>
                            <p className={d.plan.subtitle}>Plano atual</p>
                        </div>
                    </div>
                    {data.status === "trial" && (
                        <span className={d.plan.badge}>
                            <span className={`${d.subscription.dot} ${d.subscription.dotTrial}`} />
                            Avaliação
                        </span>
                    )}
                </div>
                <div className={d.plan.actions}>
                    {data.status === "trial" && (
                        <button className={d.plan.btnPrimary}>
                            <Iconify icon="solar:card-bold" width={16} />
                            Assinar agora
                        </button>
                    )}
                    <button className={d.plan.btnSecondary}>
                        <Iconify icon="solar:refresh-bold" width={16} />
                        Mudar plano
                    </button>
                </div>
                <div className={d.plan.note}>
                    <Iconify icon="solar:info-circle-bold" width={16} className={d.plan.noteIcon} />
                    <p className={d.plan.noteText}>
                        Alterações no plano podem levar até 24 horas para serem refletidas na sua conta.
                    </p>
                </div>
            </div>

            {/* Addons Card */}
            <div className={d.addons.card}>
                <div className={d.addons.header}>
                    <div className={d.addons.iconWrapper}>
                        <Iconify icon="solar:puzzle-bold" width={20} className={d.addons.icon} />
                    </div>
                    <p className={d.addons.title}>Complementos</p>
                </div>
                <div className={d.addons.list}>
                    {data.addons.map((addon) => (
                        <div key={addon.id} className={d.addons.item}>
                            <div className={d.addons.itemLeft}>
                                <div className={d.addons.itemIcon}>
                                    <Iconify icon={addon.icon} width={18} className="text-gray-600" />
                                </div>
                                <div>
                                    <p className={d.addons.itemName}>{addon.name}</p>
                                    <p className={d.addons.itemDesc}>{addon.description}</p>
                                </div>
                            </div>
                            <span className={d.addons.itemBadge}>
                                <span className={`${d.subscription.dot} ${d.subscription.dotTrial}`} />
                                Avaliação
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export function AssinaturaComponent({ cliente }) {
    const [activeTab, setActiveTab] = useState("assinatura");

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.container}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconWrapper}>
                        <Iconify icon="solar:wallet-money-bold" width={22} className={d.page.header.icon} />
                    </div>
                    <div>
                        <h1 className={d.page.header.title}>Faturamento</h1>
                        <p className={d.page.header.subtitle}>Gerencie sua assinatura, plano e complementos</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className={d.tabs.list}>
                    <TabsTrigger value="assinatura" className={d.tabs.trigger}>
                        Assinatura
                    </TabsTrigger>
                    <TabsTrigger value="faturas" className={d.tabs.trigger}>
                        Faturas
                    </TabsTrigger>
                    <TabsTrigger value="pagamento" className={d.tabs.trigger}>
                        Informações do pagamento
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="assinatura" className={d.tabs.content}>
                    <SubscriptionTab />
                </TabsContent>

                <TabsContent value="faturas" className={d.tabs.content}>
                    <TabPlaceholder
                        icon="solar:bill-list-bold"
                        title="Faturas"
                        subtitle="Em breve você poderá visualizar suas faturas aqui."
                    />
                </TabsContent>

                <TabsContent value="pagamento" className={d.tabs.content}>
                    <TabPlaceholder
                        icon="solar:card-bold"
                        title="Informações do pagamento"
                        subtitle="Em breve você poderá gerenciar seus métodos de pagamento aqui."
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
